import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Phone, Mail, Calendar } from "lucide-react";
import { toast } from "sonner";
import { postLead } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function FinalCTA() {
  const [f, setF] = useState({ name: "", email: "", phone: "", dealership: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email) {
      toast.error("Nombre y email son obligatorios");
      return;
    }
    setLoading(true);
    try {
      await postLead({ ...f, source: "final_cta_call" });
      setDone(true);
      toast.success("¡Recibido! Te llamamos en menos de 24h.");
      setF({ name: "", email: "", phone: "", dealership: "", message: "" });
    } catch {
      toast.error("Hubo un error. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="final-cta-section" className="relative py-24 lg:py-36 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]" />
      <div className="absolute inset-0 bg-radial-blue opacity-50" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <Reveal>
          <div className="font-stencil text-xs text-[#00A0A0] tracking-widest mb-4 text-center">10 / ÚLTIMA PARADA</div>
        </Reveal>

        <Reveal delay={100}>
          <h2 className="font-heading text-white text-3xl sm:text-5xl lg:text-7xl xl:text-8xl uppercase tracking-tight leading-[0.95] text-center max-w-5xl mx-auto break-words">
            EL PROBLEMA NO ES SOLO
            <br />
            <span className="text-white/40">QUE NO TE VEAN.</span>
            <br />
            ES QUE NO TE{" "}
            <span className="font-stencil text-[#00A0A0]" style={{textShadow:'3px 3px 0 rgba(255,215,0,0.5)'}}>RECUERDAN.</span>
          </h2>
        </Reveal>

        <Reveal delay={250}>
          <p className="mt-10 text-base lg:text-lg text-white/60 max-w-2xl mx-auto text-center font-body leading-relaxed">
            Construyamos una marca que genere confianza, contenido que explique tu valor y un sistema que convierta{" "}
            <span className="text-white">atención en oportunidades reales de venta</span>.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-5 gap-8 lg:gap-12 max-w-6xl mx-auto">
          <Reveal delay={300} className="lg:col-span-2">
            <div className="space-y-8">
              <ContactBlock icon={Calendar} title="LLAMADA 30 MIN" sub="Sesión estratégica gratuita" />
              <ContactBlock icon={Phone} title="+34 664 45 68 52" sub="Lun-Vie · 9:00 - 19:00" />
              <ContactBlock icon={Mail} title="fuelkersagency@gmail.com" sub="Respuesta en menos de 24h" />

              <div className="pt-8 border-t border-white/10">
                <div className="font-stencil text-xs uppercase tracking-widest text-white/40 mb-3">Sobre nosotros</div>
                <p className="text-sm text-white/60 leading-relaxed font-body">
                  <span className="text-white">FUELKERS</span> · Ecosistema de crecimiento
                  para concesionarios, importadores, detailing y talleres. Madrid · Barcelona · Valencia.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={400} className="lg:col-span-3">
            <form onSubmit={submit} className="glass p-5 sm:p-7 lg:p-10 space-y-5 relative">
              <div className="absolute -top-3 left-4 sm:left-8 px-3 py-1 bg-[#FFD700] text-black font-stencil text-[10px] tracking-widest">
                ► RESERVAR LLAMADA
              </div>

              {!done && (
                <p data-testid="form-especializacion-note" className="pt-3 -mb-1 text-[11px] text-white/40 font-body leading-relaxed">
                  Solo trabajamos con <span className="text-white/70">compra-ventas, importadores y detailing</span>. Si es tu caso, hablamos.
                </p>
              )}

              {done ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#00A0A0]/15 border border-[#00A0A0]/40 flex items-center justify-center mx-auto mb-5">
                    <ArrowRight className="w-7 h-7 text-[#00A0A0]" />
                  </div>
                  <h3 className="font-stencil text-2xl text-white mb-2">¡RESERVADO!</h3>
                  <p className="text-white/60 font-body">Te llamamos en menos de 24 horas.</p>
                  <Button onClick={() => setDone(false)} variant="ghost" className="mt-6 text-white/70">Enviar otro mensaje</Button>
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-4 pt-3">
                    <FormInput testid="contact-name" placeholder="Nombre *" value={f.name} onChange={update("name")} />
                    <FormInput testid="contact-email" placeholder="Email *" type="email" value={f.email} onChange={update("email")} />
                    <FormInput testid="contact-phone" placeholder="Teléfono" value={f.phone} onChange={update("phone")} />
                    <FormInput testid="contact-dealership" placeholder="Negocio / Marca" value={f.dealership} onChange={update("dealership")} />
                  </div>
                  <Textarea
                    data-testid="contact-message"
                    rows={4}
                    placeholder="¿Qué quieres conseguir en los próximos 90 días?"
                    value={f.message}
                    onChange={update("message")}
                    className="bg-black/40 border-white/15 text-white rounded-none resize-none focus-visible:ring-[#00A0A0] focus-visible:border-[#00A0A0]"
                  />
                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={loading}
                    className="w-full h-14 btn-angular bg-[#00A0A0] hover:bg-[#00C8C8] text-black text-xs sm:text-base font-stencil uppercase tracking-widest rounded-none group whitespace-nowrap"
                    style={{boxShadow:'5px 5px 0 #FFD700'}}
                  >
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> RESERVANDO…</>) : (<>RESERVAR LLAMADA ESTRATÉGICA <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" /></>)}
                  </Button>
                  <p className="text-[11px] text-white/35 text-center font-body">30 minutos. Sin compromiso. Revisamos tu situación y te decimos con claridad qué mejoraríamos — encajemos o no.</p>
                </>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FormInput({ testid, ...props }) {
  return (
    <Input
      data-testid={testid}
      {...props}
      className="bg-black/40 border-white/15 text-white h-12 rounded-none placeholder:text-white/35 focus-visible:ring-[#00A0A0] focus-visible:border-[#00A0A0]"
    />
  );
}

function ContactBlock({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 border border-[#00A0A0]/30 bg-[#00A0A0]/5 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#00A0A0]" strokeWidth={1.75} />
      </div>
      <div>
        <div className="font-stencil text-white text-base uppercase tracking-wide">{title}</div>
        <div className="text-xs text-white/50 mt-1 font-body">{sub}</div>
      </div>
    </div>
  );
}
